import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MagneticButton from "./MagneticButton";

const links = [
  { to: "/work", label: "Work" },
  { to: "/about", label: "About" },
  { to: "/process", label: "Process" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const solid = scrolled || open;

  return (
    <>
      {/*
        iOS 26 Safari "Liquid Glass": the fixed element itself must stay
        transparent (Safari tints/composites unreliably from a fixed bg and,
        once scrolled, shows the scrolling content behind the status bar). The
        opaque background goes on an absolute inset-0 child, which Safari DOES
        composite as real white pixels behind the status bar. The html/body
        white acts as the tint fallback. pt keeps the nav row below the notch.
      */}
      {/*
        iOS Safari (Liquid Glass): position:fixed;top:0 lands BELOW the
        translucent status bar and env(safe-area-inset-top) reports 0, so the
        status-bar band showed page content bleeding through. Bleed an opaque
        white bar ABOVE top:0 (negative top) to cover that band. Mobile only —
        desktop browsers have no such band and top:0 is the true viewport top.
      */}
      <div
        aria-hidden
        className="md:hidden fixed left-0 right-0 top-[-200px] h-[204px] bg-white z-[100] pointer-events-none"
      />
      <header
        className="fixed top-0 left-0 right-0 z-[100] pt-[env(safe-area-inset-top,0px)]"
        data-testid="site-navbar"
      >
        <div
          aria-hidden
          className={`absolute inset-0 border-b border-black/10 transition-colors duration-500 bg-white md:border-b-0 ${
            solid
              ? "md:bg-white/90 md:border-b md:backdrop-blur-sm"
              : "md:bg-transparent"
          }`}
        />
        <div className="relative z-[1] flex h-[var(--site-header-height)] w-full items-center justify-between px-6 md:px-12">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-home-link" data-cursor="home">
            <span className="w-10 h-10 inline-flex items-center justify-center bg-black shrink-0">
              <img src="/svg/Artboard 24.svg" alt="" className="h-[26px] w-auto" aria-hidden />
            </span>
            <span className="font-display text-xl tracking-tight">sauce<span className="text-[#F42B29]">.</span>studios</span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`link-line font-body text-sm uppercase tracking-[0.18em] ${location.pathname.startsWith(l.to) ? "text-[#F42B29]" : "text-black"}`}
              >
                {l.label}
              </Link>
            ))}
            <MagneticButton
              as={Link}
              to="/contact"
              className="ml-4 px-6 py-3 bg-black text-white font-body uppercase text-xs tracking-[0.18em] hover:bg-[#F42B29] transition-colors"
              data-testid="navbar-start-project-btn"
            >
              Start a project
            </MagneticButton>
          </nav>

          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden flex items-center gap-3 font-body uppercase text-xs tracking-[0.18em]"
            data-testid="mobile-menu-toggle"
          >
            {open ? "Close" : "Menu"}
            <span className="inline-block w-6 h-px bg-black" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-[90] bg-white pt-[calc(var(--site-header-offset)+1.5rem)] px-6 md:hidden"
            data-testid="mobile-menu"
          >
            <ul className="space-y-6">
              {links.map((l, i) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="font-display text-5xl uppercase tracking-tight"
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  >
                    <span className="text-sm align-top mr-3 text-[#F42B29] font-body">0{i+1}</span>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
